import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";

import {
  captureAnalyticsEvent,
  captureEventAndRedirect,
  identifyUser,
  isPostHogReady,
  resetUser,
} from "../../utils/analytics";
import { useAuth } from "../providers/AuthProvider";

/**
 * Development component to test PostHog integration
 * Only shows in development and preview mode
 */
export function PostHogTestButton() {
  const posthog = usePostHog();
  const { user } = useAuth();
  const [debugInfo, setDebugInfo] = useState<{
    isReady: boolean;
    hasInstance: boolean;
    hasKey: boolean;
    apiHost: string | undefined;
    userId: string | null;
    distinctId: string | undefined;
    lastEventResult: boolean | null;
  }>({
    isReady: false,
    hasInstance: false,
    hasKey: false,
    apiHost: undefined,
    userId: null,
    distinctId: undefined,
    lastEventResult: null,
  });

  // Only show in development and preview
  if (
    import.meta.env.VITE_NODE_ENV !== "development" &&
    import.meta.env.VITE_NODE_ENV !== "preview"
  ) {
    return null;
  }

  useEffect(() => {
    console.log("🔍 PostHog Test: Checking status...");
    const info = {
      isReady: isPostHogReady(posthog),
      hasInstance: !!posthog,
      hasKey: !!import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
      apiHost: posthog?.config?.api_host,
      userId: user,
      distinctId: posthog?.get_distinct_id?.(),
      lastEventResult: debugInfo.lastEventResult,
    };
    setDebugInfo(info);
  }, [posthog, user, debugInfo.lastEventResult]);

  const testBasicEvent = async () => {
    console.log("🔍 PostHog Test: Testing basic event capture");
    const result = await captureAnalyticsEvent(posthog, "test_basic_event", {
      timestamp: new Date().toISOString(),
      test_type: "basic",
      component: "PostHogTestButton",
    });

    setDebugInfo((prev) => ({ ...prev, lastEventResult: result }));

    if (result) {
      console.log("✅ PostHog Test: Basic event captured successfully");
      alert("Basic event captured! Check your PostHog dashboard.");
    } else {
      console.warn("❌ PostHog Test: Basic event capture failed");
      alert("Basic event capture failed! Check console for details.");
    }
  };

  const testEventWithProperties = async () => {
    console.log("🔍 PostHog Test: Testing event with custom properties");
    const result = await captureAnalyticsEvent(
      posthog,
      "test_event_with_properties",
      {
        timestamp: new Date().toISOString(),
        test_type: "properties",
        component: "PostHogTestButton",
        custom_property: "test_value",
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    );

    setDebugInfo((prev) => ({ ...prev, lastEventResult: result }));

    if (result) {
      console.log(
        "✅ PostHog Test: Event with properties captured successfully",
      );
      alert("Event with properties captured! Check your PostHog dashboard.");
    } else {
      console.warn("❌ PostHog Test: Event with properties capture failed");
      alert("Event with properties capture failed! Check console for details.");
    }
  };

  const testUserIdentification = () => {
    if (!posthog || !isPostHogReady(posthog)) {
      alert("PostHog not ready for user identification test!");
      return;
    }

    const testUserId = `test_user_${Date.now()}`;
    const testProperties = {
      email: `test.${Date.now()}@example.com`,
      name: "Test User",
      test_account: true,
      signup_date: new Date().toISOString(),
    };

    console.log("🔍 PostHog Test: Testing user identification");
    identifyUser(posthog, testUserId, testProperties);

    // Update debug info to show the new distinct ID
    setTimeout(() => {
      setDebugInfo((prev) => ({
        ...prev,
        distinctId: posthog?.get_distinct_id?.(),
      }));
    }, 500);

    alert(
      "User identification test completed! Check console and PostHog dashboard.",
    );
  };

  const testUserReset = () => {
    console.log("🔍 PostHog Test: Testing user reset");
    resetUser(posthog);

    // Update debug info to show the reset distinct ID
    setTimeout(() => {
      setDebugInfo((prev) => ({
        ...prev,
        distinctId: posthog?.get_distinct_id?.(),
      }));
    }, 500);

    alert("User reset test completed! Check console and PostHog dashboard.");
  };

  const testFeatureFlag = () => {
    if (!posthog || !isPostHogReady(posthog)) {
      alert("PostHog not ready for feature flag test!");
      return;
    }

    console.log("🔍 PostHog Test: Testing feature flag check");

    // Test a dummy feature flag
    const testFlagKey = "test_feature_flag";
    const flagValue = posthog.isFeatureEnabled(testFlagKey);

    console.log(
      `🔍 PostHog Test: Feature flag '${testFlagKey}' value:`,
      flagValue,
    );
    alert(
      `Feature flag '${testFlagKey}' is ${flagValue ? "enabled" : "disabled"}. Check console for details.`,
    );
  };

  const testEventAndRedirect = async () => {
    if (!posthog || !isPostHogReady(posthog)) {
      alert("PostHog not ready for redirect test!");
      return;
    }

    const confirmed = confirm(
      "This will capture an event and redirect to the home page. Continue?",
    );

    if (confirmed) {
      console.log("🔍 PostHog Test: Testing event capture with redirect");
      await captureEventAndRedirect(
        posthog,
        "test_event_before_redirect",
        "/",
        {
          test_type: "redirect",
          component: "PostHogTestButton",
          timestamp: new Date().toISOString(),
        },
      );
    }
  };

  const testConnectionDiagnostics = () => {
    console.log("🔍 PostHog Test: Running connection diagnostics");

    const diagnostics = {
      instance: !!posthog,
      ready: isPostHogReady(posthog),
      apiKey: !!import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
      apiHost: posthog?.config?.api_host,
      captureMethod: typeof posthog?.capture,
      identifyMethod: typeof posthog?.identify,
      featureEnabledMethod: typeof posthog?.isFeatureEnabled,
      distinctId: posthog?.get_distinct_id?.(),
      sessionId: posthog?.get_session_id?.(),
      config: posthog?.config,
    };

    console.table(diagnostics);
    alert(
      "Connection diagnostics logged to console. Press F12 to view details.",
    );
  };

  const getStatusColor = (status: boolean) =>
    status ? "text-green-400" : "text-red-400";
  const getStatusIcon = (status: boolean) => (status ? "✅" : "❌");

  return (
    <div className="fixed bottom-4 left-4 z-50 bg-gray-800 text-white p-4 rounded-lg shadow-lg max-w-xs">
      <h3 className="text-sm font-semibold mb-3 text-blue-400">
        PostHog Test (Dev Only)
      </h3>

      {/* Status Display */}
      <div className="text-xs mb-3 space-y-1 font-mono">
        <div className={getStatusColor(debugInfo.isReady)}>
          {getStatusIcon(debugInfo.isReady)} Ready:{" "}
          {debugInfo.isReady.toString()}
        </div>
        <div className={getStatusColor(debugInfo.hasInstance)}>
          {getStatusIcon(debugInfo.hasInstance)} Instance:{" "}
          {debugInfo.hasInstance.toString()}
        </div>
        <div className={getStatusColor(debugInfo.hasKey)}>
          {getStatusIcon(debugInfo.hasKey)} API Key:{" "}
          {debugInfo.hasKey.toString()}
        </div>
        <div className="text-gray-300">Host: {debugInfo.apiHost || "❌"}</div>
        <div className="text-gray-300">User: {debugInfo.userId || "None"}</div>
        <div className="text-gray-300">ID: {debugInfo.distinctId || "❌"}</div>
        {debugInfo.lastEventResult !== null && (
          <div className={getStatusColor(debugInfo.lastEventResult)}>
            Last Event: {getStatusIcon(debugInfo.lastEventResult)}
          </div>
        )}
      </div>

      {/* Test Buttons */}
      <div className="space-y-2">
        <button
          onClick={testConnectionDiagnostics}
          className="block w-full text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
        >
          Connection Test
        </button>
        <button
          onClick={testBasicEvent}
          className="block w-full text-xs bg-green-600 hover:bg-green-700 px-2 py-1 rounded"
        >
          Basic Event
        </button>
        <button
          onClick={testEventWithProperties}
          className="block w-full text-xs bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded"
        >
          Event + Properties
        </button>
        <button
          onClick={testUserIdentification}
          className="block w-full text-xs bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded"
        >
          Identify User
        </button>
        <button
          onClick={testUserReset}
          className="block w-full text-xs bg-orange-600 hover:bg-orange-700 px-2 py-1 rounded"
        >
          Reset User
        </button>
        <button
          onClick={testFeatureFlag}
          className="block w-full text-xs bg-indigo-600 hover:bg-indigo-700 px-2 py-1 rounded"
        >
          Feature Flag
        </button>
        <button
          onClick={testEventAndRedirect}
          className="block w-full text-xs bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
        >
          Event + Redirect
        </button>
      </div>
    </div>
  );
}
